import { useState, useEffect } from "react";

const tickRate = 14;

const playerSize = [4, 2];
const alienSize = [3, 3];
const areaSize = [64, 64];
const lazerSize = [0.5, 2];

type SpaceGameHook = [
  number[],
  [number, number],
  number[],
  {
    position: number[];
    variation: number;
  }[],
  number[],
  {
    direction: string;
    time: number;
    position: number[];
  }[],
  number[],
  {
    time: number;
    position: number[];
    size: number;
    type: number;
  }[],
  boolean,
  number,
  React.Dispatch<React.SetStateAction<boolean>>
];

export default function useSpaceGame(): SpaceGameHook {
  const [playerPosition, setPlayerPosition] = useState<[number, number]>([
    areaSize[0] - playerSize[0],
    122
  ]);
  const [playerVelocity] = useState(2);

  const [maxAliens, setMaxAliens] = useState(24);
  const [aliens, setAliens] = useState([
    { position: [-10, -10], variation: 0 }
  ]);
  const [alienPatron, setAlienPatron] = useState(0);
  const [aliensVelocity, setAliensVelocity] = useState(1000);

  const [lazers, setLazers] = useState([
    { direction: "", time: 0, position: [-10, -10] }
  ]);
  const [explosions, setExplosions] = useState([
    { time: 0, position: [-10, -10], size: 0, type: 0 }
  ]);

  //KeyMapping
  const [keyEventReady, setKeyEventReady] = useState(false);
  const [keysState] = useState([
    {
      keyCode: "KeyA",
      down: false,
      up: false,
      once: false
    },
    {
      keyCode: "KeyD",
      down: false,
      up: false,
      once: false
    },
    {
      keyCode: "ArrowLeft",
      down: false,
      up: false,
      once: false
    },
    {
      keyCode: "ArrowRight",
      down: false,
      up: false,
      once: false
    },
    {
      keyCode: "Space",
      down: false,
      up: false,
      once: false
    }
  ]);

  const [points, setPoints] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [roundStarted, setRoundStarted] = useState(false);
  const [gameOver, setGameOver] = useState(true);

  const [loop, setLoop] = useState(0);
  const [aliensLoop, setAliensLoop] = useState(0);

  //Eventos de teclas
  useEffect(() => {
    if (!keyEventReady) {
      //Al presionar la tecla
      document.addEventListener("keydown", (e: KeyboardEvent) => {
        const k = keysState.find(k => k.keyCode === e.code);
        if (k) {
          k.down = true;
          k.up = false;
        }
      });
      //Al soltar tecla
      document.addEventListener("keyup", (e: KeyboardEvent) => {
        const k = keysState.find(k => k.keyCode === e.code);
        if (k) {
          k.down = false;
          k.up = true;
          k.once = false;
        }
      });
      setKeyEventReady(true);
    }
  }, [keyEventReady, keysState, gameStarted, roundStarted]);

  //Game Start
  useEffect(() => {
    if (gameStarted && !roundStarted) {
      setAliens([]);
      setExplosions([]);
      setLazers([]);

      const _aliens = [];
      for (let y = 0; y < 4; y++) {
        for (let x = 0; x < maxAliens / 4; x++) {
          const xPos = areaSize[0] / 2 + maxAliens / 4 + x * 8;
          const yPos = areaSize[1] / 2 - maxAliens / 4 + y * 8;
          _aliens.push({
            position: [xPos, yPos],
            variation: getRandomInt(0, 4)
          });
        }
      }
      setAliens([..._aliens]);
      setRoundStarted(true);
    }
    if (gameOver && gameStarted) {
      setGameOver(false);
      setPoints(0);
      setAliensVelocity(1000);
    }
  }, [gameStarted, roundStarted, maxAliens, gameOver]);

  //Game Loop
  useEffect(() => {
    if (loop === 0 && !gameOver) {
      const l = window.setTimeout(() => {
        //Comprobacion de teclas
        const k = keysState.find(x => x.down);
        if (k) {
          switch (k.keyCode) {
            case "ArrowLeft":
            case "KeyA":
              setPlayerPosition(x => [x[0] - 1 * playerVelocity, x[1]]);
              break;

            case "ArrowRight":
            case "KeyD":
              setPlayerPosition(x => [x[0] + 1 * playerVelocity, x[1]]);
              break;
            case "Space":
              if (!k.once) {
                const _shoots = lazers;
                _shoots.push({
                  direction: "up",
                  time: areaSize[1] / 2,
                  position: [
                    playerPosition[0] + playerSize[0] - 0.5,
                    playerPosition[1] + playerSize[1]
                  ]
                });
                k.once = true;
              }
              break;
          }
        }

        if (aliensLoop === 0 && roundStarted) {
          const al = window.setTimeout(() => {
            switch (alienPatron) {
              case 0:
                aliens.forEach(alien => {
                  alien.position[0] += 16;
                });
                setAlienPatron(1);
                break;
              case 1:
                aliens.forEach(alien => {
                  alien.position[1] += 8;
                });
                setAlienPatron(2);
                break;
              case 2:
                aliens.forEach(alien => {
                  alien.position[0] -= 16;
                });
                setAlienPatron(3);
                break;
              case 3:
                aliens.forEach(alien => {
                  alien.position[1] += 8;
                });
                setAlienPatron(0);
                break;
            }
            setAliensLoop(0);
            if (aliens.find(x => x.position[1] >= playerPosition[1])) {
              setGameStarted(false);
              setRoundStarted(false);
              setGameOver(true);
            }
          }, aliensVelocity);
          setAliensLoop(al);
        }

        const _lazers = lazers;
        _lazers.forEach((l, index) => {
          //Dependiendo la direccion del laser se suma o resta 1 punto al eje Y
          switch (l.direction) {
            case "up":
              l.position[1] -= 4;
              break;

            case "down":
              l.position[1] += 4;
              break;
          }
          //Se reduce 1 punto de tiempo de vida
          l.time--;

          //Se comprueba si algun laser golpeo a algun alien
          aliens.forEach((alien, index) => {
            if (
              l.position[0] < alien.position[0] + alienSize[0] * 2 &&
              l.position[0] + lazerSize[1] > alien.position[0] &&
              l.position[1] < alien.position[1] + alienSize[1] * 2 &&
              lazerSize[0] + l.position[1] > alien.position[1]
            ) {
              const _explosions = explosions;
              _explosions.push({
                time: 5,
                position: [
                  alien.position[0] + 1,
                  alien.position[1] + alienSize[1] - 1
                ],
                size: 1,
                type: 1
              });
              aliens.splice(index, 1);
              l.time = 0;

              setPoints(x => x + 100);
              //Si se derrotaron a todos los aliens, se reinicia,
              //se agregan mas aliens y se aumenta su velocidad de desplazamiento
              if (aliens.length <= 0) {
                setGameStarted(false);
                setAliens([]);
                setExplosions([]);
                setLazers([]);
                window.setTimeout(() => {
                  setRoundStarted(false);
                  setGameStarted(true);
                  setMaxAliens(x => (x < 30 ? x + 2 : x));
                  setAliensVelocity(x => (x > 200 ? x - 100 : x));
                }, 1000);
              }
            }
          });
          //Si el tiempo del laser es menor (o igual) a 0, se destruye
          //y se deja una explosion en su lugar
          if (l.time <= 0) {
            const _explosions = explosions;
            _explosions.push({
              time: 4,
              position: [l.position[0] - 1, l.position[1] + lazerSize[1] - 1],
              size: 1,
              type: 0
            });
            setExplosions([..._explosions]);

            _lazers.splice(index, 1);
          }
        });
        setLazers([..._lazers]);

        //A cada explosion en el canvas se le aumenta el tamaño y reduce su tiempo de vida
        const _explosions = explosions;
        _explosions.forEach((e, index) => {
          e.time--;
          e.size++;
          if (e.time <= 0) _explosions.splice(index, 1);
        });
        setExplosions([..._explosions]);

        setLoop(0);
      }, tickRate);
      setLoop(l);
    }
  }, [
    keysState,
    loop,
    playerPosition,
    playerVelocity,
    lazers,
    aliens,
    aliensLoop,
    alienPatron,
    aliensVelocity,
    explosions,
    gameStarted,
    roundStarted,
    gameOver
  ]);

  return [
    areaSize,
    playerPosition,
    playerSize,
    aliens,
    alienSize,
    lazers,
    lazerSize,
    explosions,
    gameOver,
    points,
    setGameStarted
  ];
}

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min)) + min;
}
